/**
 * Single source for whether analytics collection is allowed.
 * When `COOKIE_CONSENT_REQUIRED_FOR_ANALYTICS` is true, user must have accepted in consentStore
 * (and not used privacy opt-out). When false, only the opt-out applies.
 */
import { COOKIE_CONSENT_REQUIRED_FOR_ANALYTICS } from './consentFeatureFlags';
import { isConsentAccepted } from './consentStore';

const OPT_OUT_STORAGE_KEY = 'pmc_analytics_opt_out';

/** Fired when opt-out changes so hooks (e.g. useAnalytics) can react. */
export const ANALYTICS_PREFERENCE_CHANGE_EVENT = 'pmc-analytics-preference-changed';

export function getAnalyticsUserOptOut(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(OPT_OUT_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * True when events may be sent: passes consent gate (if enabled) and has not opted out in privacy settings.
 */
export function isAnalyticsCollectionEnabled(): boolean {
  const consentOk = !COOKIE_CONSENT_REQUIRED_FOR_ANALYTICS || isConsentAccepted();
  return consentOk && !getAnalyticsUserOptOut();
}

/**
 * Persist user opt-out. Does not reset providers — call `setAnalyticsUserOptOut` from the hub for that.
 */
export function persistAnalyticsUserOptOut(optOut: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    if (optOut) {
      window.localStorage.setItem(OPT_OUT_STORAGE_KEY, '1');
    } else {
      window.localStorage.removeItem(OPT_OUT_STORAGE_KEY);
    }
  } catch {
    /* private mode / quota */
  }
  window.dispatchEvent(new CustomEvent(ANALYTICS_PREFERENCE_CHANGE_EVENT));
}

export function subscribeAnalyticsPreferenceChange(listener: () => void): () => void {
  if (typeof window === 'undefined') {
    return () => {};
  }
  const wrapped = () => listener();
  window.addEventListener(ANALYTICS_PREFERENCE_CHANGE_EVENT, wrapped);
  return () => window.removeEventListener(ANALYTICS_PREFERENCE_CHANGE_EVENT, wrapped);
}
